import React, { useEffect, useState } from "react";
import { api, fileUrl } from "./api";

const DesignModule = ({ project }) => {
  const projectId = project?._id || project?.id;

  const [data, setData] = useState(null);
  const [openFolder, setOpenFolder] = useState(null);
  const [uploadingFolderId, setUploadingFolderId] = useState(null);

  const getCurrentUserName = () => {
    try {
      const userData = localStorage.getItem("user");
      const parsedUser = userData ? JSON.parse(userData) : null;

      return (
        parsedUser?.name ||
        parsedUser?.userName ||
        localStorage.getItem("userName") ||
        localStorage.getItem("name") ||
        "Unknown User"
      );
    } catch {
      return (
        localStorage.getItem("userName") ||
        localStorage.getItem("name") ||
        "Unknown User"
      );
    }
  };

  const loadDesign = async () => {
    if (!projectId) return;

    try {
      const res = await api.get(`/drawing-design/${projectId}`);
      setData(res.data);

      const firstOpen = res.data.folders?.find((f) => f.isOpen);
      setOpenFolder(firstOpen?._id || res.data.folders?.[0]?._id || null);
    } catch (error) {
      console.error("Load design error:", error);
    }
  };

  useEffect(() => {
    loadDesign();
  }, [projectId]);

  const uploadFiles = async (folderId, files) => {
    if (!projectId) {
      alert("Project ID missing");
      return;
    }

    if (!files || files.length === 0) return;

    try {
      setUploadingFolderId(folderId);

      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });

      formData.append("uploadedBy", getCurrentUserName());

      const res = await api.post(
        `/drawing-design/${projectId}/folders/${folderId}/upload`,
        formData,
      );

      setData(res.data);
      setOpenFolder(folderId);
    } catch (error) {
      console.error("Upload file error:", error);
      alert("File upload failed");
    } finally {
      setUploadingFolderId(null);
    }
  };

  if (!data) return <div style={styles.loading}>Loading Design...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h3 style={styles.title}>Design Files</h3>
          <p style={styles.subtitle}>
            Manage all design drawings, renders, MOM and material files
          </p>
        </div>

        <div style={styles.totalBadge}>
          Total Files:{" "}
          {data.folders?.reduce(
            (total, folder) => total + (folder.files?.length || 0),
            0,
          )}
        </div>
      </div>

      <div style={styles.tabsWrap}>
        <div style={styles.tabs}>
          {data.folders?.map((folder) => {
            const isActive = openFolder === folder._id;

            return (
              <button
                key={folder._id}
                onClick={() => setOpenFolder(folder._id)}
                style={{
                  ...styles.tab,
                  ...(isActive ? styles.activeTab : {}),
                }}
              >
                <span>{folder.folderName}</span>
                <span
                  style={{
                    ...styles.count,
                    ...(isActive ? styles.activeCount : {}),
                  }}
                >
                  {folder.files?.length || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name of File</th>
              <th style={styles.th}>Type of File</th>
              <th style={styles.th}>Uploaded By</th>
              <th style={styles.th}>Uploaded On</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {data.folders?.map((folder) => (
              <React.Fragment key={folder._id}>
                <tr style={styles.folderRow}>
                  <td colSpan="6" style={styles.folderCell}>
                    <button
                      onClick={() => setOpenFolder(folder._id)}
                      style={styles.arrowBtn}
                    >
                      {openFolder === folder._id ? "⌄" : "›"}
                    </button>

                    <div style={{ flex: 1 }}>
                      <div style={styles.folderName}>{folder.folderName}</div>
                      <div style={styles.folderMeta}>
                        {folder.files?.length || 0} file uploaded
                      </div>
                    </div>

                    <label
                      style={{
                        ...styles.uploadBtn,
                        opacity: uploadingFolderId === folder._id ? 0.6 : 1,
                        pointerEvents:
                          uploadingFolderId === folder._id ? "none" : "auto",
                      }}
                    >
                      {uploadingFolderId === folder._id
                        ? "Uploading..."
                        : "+ Upload"}

                      <input
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          uploadFiles(folder._id, e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </td>
                </tr>

                {openFolder === folder._id &&
                  folder.files?.map((file) => (
                    <tr key={file._id} style={styles.fileRow}>
                      <td style={styles.td}>
                        <a
                          href={fileUrl(file.fileUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.fileLink}
                        >
                          <span style={styles.fileIcon}>📄</span>
                          {file.fileName}
                        </a>
                      </td>

                      <td style={styles.td}>
                        <span style={styles.typeBadge}>
                          {file.fileType || "-"}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <span style={styles.userText}>
                          {file.uploadedBy || "Unknown User"}
                        </span>
                      </td>

                      <td style={styles.td}>
                        {file.uploadedOn
                          ? new Date(file.uploadedOn).toLocaleString()
                          : "-"}
                      </td>

                      <td style={styles.td}>
                        <span style={styles.status}>
                          {file.status || "Draft"}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <button style={styles.actionBtn}>⋮</button>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
  },

  loading: {
    padding: 30,
    color: "#475569",
    fontWeight: 800,
  },

  topBar: {
    padding: "20px 22px",
    background: "linear-gradient(90deg, #f8fafc, #eef2ff)",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 900,
    color: "#0f172a",
  },

  subtitle: {
    margin: "5px 0 0",
    fontSize: 14,
    color: "#64748b",
    fontWeight: 600,
  },

  totalBadge: {
    padding: "9px 14px",
    borderRadius: "999px",
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 900,
  },

  tabsWrap: {
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    padding: "12px 14px",
  },

  tabs: {
    display: "flex",
    gap: "10px",
    overflowX: "auto",
    paddingBottom: "4px",
    scrollbarWidth: "thin",
  },

  tab: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    padding: "10px 15px",
    minHeight: 42,
    borderRadius: "999px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 800,
    color: "#1e293b",
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
  },

  activeTab: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
  },

  count: {
    background: "#e2e8f0",
    color: "#0f172a",
    borderRadius: "999px",
    padding: "2px 8px",
    fontSize: "12px",
    fontWeight: 900,
  },

  activeCount: {
    background: "#ffffff",
    color: "#2563eb",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: "1050px",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },

  th: {
    textAlign: "left",
    padding: "15px 16px",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  td: {
    padding: "15px 16px",
    borderBottom: "1px solid #e5e7eb",
    color: "#334155",
    verticalAlign: "middle",
  },

  folderRow: {
    background: "#ffffff",
  },

  folderCell: {
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#ffffff",
  },

  folderName: {
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: 900,
  },

  folderMeta: {
    marginTop: 3,
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
  },

  arrowBtn: {
    width: 34,
    height: 34,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: "10px",
    fontSize: 20,
    cursor: "pointer",
    color: "#334155",
  },

  uploadBtn: {
    color: "#ffffff",
    background: "#ef4444",
    cursor: "pointer",
    fontWeight: 900,
    padding: "9px 14px",
    borderRadius: "12px",
    fontSize: "13px",
    boxShadow: "0 6px 14px rgba(239,68,68,0.25)",
  },

  fileRow: {
    background: "#ffffff",
  },

  fileLink: {
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  fileIcon: {
    width: 30,
    height: 30,
    borderRadius: "10px",
    background: "#eff6ff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  typeBadge: {
    background: "#f1f5f9",
    color: "#334155",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 800,
  },

  userText: {
    fontWeight: 800,
    color: "#0f172a",
  },

  status: {
    color: "#ea580c",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: 900,
  },

  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "18px",
    color: "#475569",
  },
};

export default DesignModule;
